import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useConfirm } from '../mailing/MailingDialogs';
import { useFeather } from '../shell/useFeather';
import {
  listMailingSegments,
  createMailingSegment,
  updateMailingSegment,
  deleteMailingSegment,
  getMailingContactsStatus,
  getMailingSegmentContacts,
  updateMailingContactVocative,
  excludeFromMailingSegment,
  includeInMailingSegment,
  type MailingExcludedContact,
  AffilmasterApiError,
  setMailingGroupsSpace,
  type MailingSegmentItem,
  type MailingContactsStatus,
  type MailingContactRow,
} from '../api/affilmaster';
import './affilmaster-mailing-segmenty.css';
import { klientSlug } from './AffilmasterKlientiPage';
import { mailingApi, type ContactMailStatus } from '../api/mailing';
import { MailStatusDot } from '../mailing/MailStatusDot';
import { CompactSelect } from '../components/arc/compact-select/compact-select';

type GroupsSpace = 'affilmaster' | 'affilplatform';

const intFmt = (n: number) => n.toLocaleString('cs-CZ');

function sourceLabel(source: string, space: GroupsSpace, manual = 'Ruční e-mail'): string {
  if (source === 'affiliate') return 'Affiliate';
  if (source === 'manual') return manual;
  return space === 'affilplatform' ? 'Klient' : 'Inzerent';
}
const COUNTRIES = ['Vše', 'CZ', 'SK'] as const;

export function AffilmasterMailingSegmentyPage({ space = 'affilmaster' }: { space?: GroupsSpace } = {}) {
  setMailingGroupsSpace(space);
  const [country, setCountry] = useState<string>('Vše');
  const [stav, setStav] = useState('all');
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<MailingSegmentItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<MailingSegmentItem | 'new' | null>(null);
  const [detail, setDetail] = useState<MailingSegmentItem | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [status, setStatus] = useState<MailingContactsStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      listMailingSegments({
        search: query.trim() || undefined,
        country: country !== 'Vše' ? country : undefined,
        stav: stav !== 'all' ? stav : undefined,
      })
        .then((res) => {
          if (cancelled) return;
          setRows(res.items);
          setTotal(res.total);
          setError(null);
          getMailingContactsStatus().then((st) => !cancelled && setStatus(st)).catch(() => undefined);
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          setError(e instanceof AffilmasterApiError ? e.message : 'Načtení selhalo');
          setRows([]);
          setTotal(0);
        })
        .finally(() => !cancelled && setLoading(false));
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [country, stav, query, reloadKey]);

  useFeather([rows, editing, detail, status, query, error]);

  function reset() {
    setCountry('Vše');
    setStav('all');
    setQuery('');
  }

  const statusTitle = status?.perSourceCountry
    .map((s) => `${sourceLabel(s.source, space, 'Ruční')} ${s.country ?? '-'}: ${intFmt(s.n)}`)
    .join(' · ');
  const filtersActive = country !== 'Vše' || stav !== 'all' || query.trim().length > 0;

  return (
    <section className="am-mailing-segments">
      <header className="prototype-page-head am-segment-page-head">
        <div>
          <div className="prototype-eyebrow">MAILING</div>
          <h1>Skupiny</h1>
          <p>Skupiny kontaktů a pravidla pro mailingové kampaně</p>
        </div>
        <div className="am-segment-head-actions">
          <button type="button" className="am-segment-primary" onClick={() => setEditing('new')}>
            <i data-feather="plus" aria-hidden="true" />
            <span>Nová skupina</span>
          </button>
        </div>
      </header>

      <div className="am-segment-toolbar" aria-label="Filtry skupin">
        <div className="am-segment-toolbar-controls">
          <div className="am-segment-market-filter">
            <span>Trh</span>
            <div role="group" aria-label="Vybrat trh">
              {COUNTRIES.map((c) => (
                <button
                  type="button"
                  key={c}
                  className={country === c ? 'active' : ''}
                  aria-pressed={country === c}
                  onClick={() => setCountry(c)}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="am-segment-status-filter">
            <span>Stav</span>
            <CompactSelect
              className="am-segment-status-select"
              value={stav}
              ariaLabel="Stav"
              onChange={setStav}
              options={[
                { value: 'all', label: 'Všechny' },
                { value: 'active', label: 'Aktivní' },
                { value: 'inactive', label: 'Neaktivní' },
                { value: 'hidden', label: 'Skryté v listu' },
                { value: 'in_list', label: 'Viditelné v listu' },
              ]}
            />
          </div>

          <button type="button" className="am-segment-reset" onClick={reset} disabled={!filtersActive}>
            Vyčistit
          </button>
        </div>

        <div className="am-segment-search">
          <i data-feather="search" aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Hledat název nebo pravidlo…"
            aria-label="Hledat skupinu"
          />
          {query && (
            <button type="button" onClick={() => setQuery('')} aria-label="Vymazat hledání">
              <i data-feather="x" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      <div className="am-segment-list-meta" aria-live="polite">
        <span>{loading ? 'Načítám skupiny…' : `${intFmt(total)} ${total === 1 ? 'skupina' : total > 1 && total < 5 ? 'skupiny' : 'skupin'}`}</span>
        {status ? (
          <span className="am-segment-source" title={statusTitle || undefined}>
            <i data-feather="database" aria-hidden="true" />
            <strong>{intFmt(status.total)}</strong> {status.total === 1 ? 'kontakt' : status.total > 1 && status.total < 5 ? 'kontakty' : 'kontaktů'} v adresáři
            {status.lastUpdated && <> · aktualizováno {new Date(status.lastUpdated).toLocaleString('cs-CZ')}</>}
          </span>
        ) : null}
      </div>

      <div className="am-segment-list">
        <table className="am-segment-table sticky-thead">
          <thead>
            <tr>
              <th>Skupina</th>
              <th>Trh</th>
              <th className="text-end">Kontakty</th>
              <th>Pravidla</th>
              <th className="text-end">Priorita</th>
              <th>Stav</th>
              <th>V seznamu</th>
              <th aria-label="Akce" />
            </tr>
          </thead>
          <tbody>
            {error ? (
              <tr className="am-segment-message-row">
                <td colSpan={8} className="am-segment-empty is-error">
                  <div className="am-segment-error-state" role="alert">
                    <span className="am-segment-error-icon" aria-hidden="true">
                      <i data-feather="alert-circle" />
                    </span>
                    <div>
                      <strong>Skupiny se nepodařilo načíst</strong>
                      <small>{error}</small>
                    </div>
                    <button type="button" onClick={() => setReloadKey((key) => key + 1)}>
                      <i data-feather="refresh-cw" aria-hidden="true" />
                      Načíst znovu
                    </button>
                  </div>
                </td>
              </tr>
            ) : loading ? (
              <tr className="am-segment-message-row"><td colSpan={8} className="am-segment-empty">Načítám skupiny…</td></tr>
            ) : rows.length === 0 ? (
              <tr className="am-segment-message-row"><td colSpan={8} className="am-segment-empty">Žádná skupina neodpovídá vybraným filtrům.</td></tr>
            ) : (
              rows.map((s) => (
                <tr
                  key={s.id}
                  tabIndex={0}
                  aria-label={`Otevřít detail skupiny ${s.name}`}
                  onClick={() => setDetail(s)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setDetail(s);
                    }
                  }}
                >
                  <td className="am-segment-name-cell" data-label="Skupina">
                    <span className="am-segment-mark" style={{ backgroundColor: s.color }} aria-hidden="true">
                      {s.name.slice(0, 2).toUpperCase()}
                    </span>
                    <span>
                      <strong>{s.name}</strong>
                      <small>Skupina #{s.id}</small>
                    </span>
                  </td>
                  <td className="am-segment-market-cell" data-label="Trh"><span className="am-segment-country">{s.country}</span></td>
                  <td className="am-segment-contact-count text-end" data-label="Kontakty">
                    <strong>{intFmt(s.contactCount)}</strong>
                    {(s.missingVocative ?? 0) > 0 && (
                      <button
                        type="button"
                        className="am-segment-vocative-flag"
                        title="Zobrazit kontakty bez vokativu"
                        onClick={(e) => { e.stopPropagation(); setDetail(s); }}
                      >
                        {intFmt(s.missingVocative ?? 0)} bez vokativu
                      </button>
                    )}
                  </td>
                  <td className="am-segment-rule-cell" data-label="Pravidla">
                    <strong>{s.ruleCount} {s.ruleCount === 1 ? 'pravidlo' : s.ruleCount < 5 ? 'pravidla' : 'pravidel'}</strong>
                    <small title={s.ruleSummary ?? undefined}>{s.ruleSummary ?? 'Bez podmínek'}</small>
                  </td>
                  <td className="am-segment-priority text-end" data-label="Priorita">{s.priority}</td>
                  <td className="am-segment-state-cell" data-label="Stav">
                    <span className={`am-segment-state ${s.isActive ? 'is-active' : ''}`}>{s.isActive ? 'Aktivní' : 'Neaktivní'}</span>
                  </td>
                  <td className="am-segment-visibility-cell" data-label="V seznamu">
                    <span className={`am-segment-boolean ${s.inList ? 'is-visible' : ''}`}>
                      <i data-feather={s.inList ? 'check' : 'minus'} aria-hidden="true" />{s.inList ? 'Ano' : 'Ne'}
                    </span>
                  </td>
                  <td className="am-segment-action-cell">
                    <button
                      type="button"
                      className="am-segment-row-action"
                      title="Otevřít detail"
                      aria-label={`Otevřít detail skupiny ${s.name}`}
                      onClick={(e) => { e.stopPropagation(); setDetail(s); }}
                    >
                      <i data-feather="chevron-right" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p className="am-segment-footnote">Počet kontaktů se přepočítává při uložení skupiny.</p>

      {detail && (
        <SegmentDetailModal
          space={space}
          segment={detail}
          onClose={() => setDetail(null)}
          onEdit={() => { setEditing(detail); setDetail(null); }}
          onChanged={() => setReloadKey((k) => k + 1)}
        />
      )}
      {editing && (
        <SegmentEditModal
          metrics={space === 'affilplatform' ? AP_RULE_METRICS : RULE_METRICS}
          segment={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); setReloadKey((k) => k + 1); }}
        />
      )}
    </section>
  );
}

function cardStyle(maxWidth: number): CSSProperties {
  return { maxWidth };
}

function ModalShell({ title, maxWidth, onClose, footer, children, className = '' }: {
  title: string; maxWidth: number; onClose: () => void; footer: ReactNode; children: ReactNode; className?: string;
}) {
  return (
    <div className="prototype-modal-backdrop am-segment-modal-backdrop" onClick={onClose}>
      <div className={`prototype-modal-card am-segment-modal-card ${className}`.trim()} style={cardStyle(maxWidth)} onClick={(e) => e.stopPropagation()}>
        <div className="prototype-modal-head am-segment-modal-head">
          <div><div className="prototype-eyebrow">MAILING</div><h2>{title}</h2></div>
          <button className="prototype-modal-close am-segment-modal-close" type="button" title="Zavřít" onClick={onClose} aria-label="Zavřít">
            <i data-feather="x" aria-hidden="true" />
          </button>
        </div>
        <div className="am-segment-modal-body">{children}</div>
        <div className="am-segment-modal-footer">{footer}</div>
      </div>
    </div>
  );
}

function cardHref(c: MailingContactRow, space: GroupsSpace): string | null {
  if (c.source === 'manual' || !c.parentId) return null;
  if (space === 'affilplatform') {
    const base = window.location.hostname.split('.')[0] === 'affilplatform' ? '' : '/affilplatform';
    return `${base}/klienti/seznam/${encodeURIComponent(c.parentId)}`;
  }
  const prefix = window.location.hostname.split('.')[0] === 'affilmaster' ? '' : '/affilmaster';
  return c.source === 'affiliate'
    ? `${prefix}/affiliates/${encodeURIComponent(c.parentId)}`
    : c.parentName ? `${prefix}/klienti-seznam/${encodeURIComponent(klientSlug(c.parentName))}` : null;
}

function VocativeCell({ contact: c, onSaved }: { contact: MailingContactRow & { vocativeOk: boolean }; onSaved: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(c.vocative ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true); setErr(null);
    try {
      const r = await updateMailingContactVocative({ source: c.source, sourceId: c.sourceId, vocative: value });
      onSaved(r.vocative);
      setEditing(false);
    } catch (e) {
      setErr(e instanceof AffilmasterApiError ? e.message : 'Uložení selhalo');
    } finally {
      setBusy(false);
    }
  }

  if (c.source === 'manual' && !c.vocativeOk) {
    return (
      <span className="am-vocative-value is-missing" title="Doplň vokativ v pravidle skupiny za e-mail">
        <strong>Chybí</strong>
        <small>Upravit v pravidle</small>
      </span>
    );
  }
  if (!editing) {
    return (
      <span className={`am-vocative-value${c.vocativeOk ? '' : ' is-missing'}`}>
        {c.vocativeOk ? <strong>{c.vocative}</strong> : <strong>{c.vocative ? `Neplatný: ${c.vocative}` : 'Chybí'}</strong>}
        {c.source !== 'manual' && (
          <button type="button" className="am-vocative-edit" onClick={() => { setValue(c.vocative ?? ''); setEditing(true); }}>
            {c.vocativeOk ? 'Upravit' : 'Doplnit'}
          </button>
        )}
      </span>
    );
  }
  return (
    <span className="am-vocative-editor">
      <span>
        <input
          value={value}
          autoFocus
          placeholder="např. Jane"
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
        />
        <button type="button" className="am-vocative-save" disabled={busy || !value.trim()} onClick={save}>{busy ? '…' : 'Uložit'}</button>
        <button type="button" className="am-vocative-cancel" disabled={busy} onClick={() => setEditing(false)} aria-label="Zrušit úpravu">×</button>
      </span>
      {err && <small>{err}</small>}
    </span>
  );
}

function SegmentDetailModal({ segment: s, space, onClose, onEdit, onChanged }: {
  segment: MailingSegmentItem; space: GroupsSpace; onClose: () => void; onEdit: () => void; onChanged?: () => void;
}) {
  const parentLabel = space === 'affilplatform' ? 'Klient' : 'Firma / affiliate';
  const { confirm, dialog } = useConfirm();
  const [contacts, setContacts] = useState<Array<MailingContactRow & { vocativeOk: boolean }> | null>(null);
  const [missingVocative, setMissingVocative] = useState(0);
  const [tab, setTab] = useState<'missing' | 'all' | 'excluded'>((s.missingVocative ?? 0) > 0 ? 'missing' : 'all');
  const [excludeErr, setExcludeErr] = useState<string | null>(null);
  const [excluded, setExcluded] = useState<MailingExcludedContact[]>([]);
  const [reload, setReload] = useState(0);
  const [mailStatus, setMailStatus] = useState<Map<string, ContactMailStatus>>(new Map());

  async function loadMailStatus(rows: Array<MailingContactRow>) {
    const emails = [...new Set(rows.map((r) => (r.email ?? '').trim().toLowerCase()).filter(Boolean))];
    const next = new Map<string, ContactMailStatus>();
    for (let i = 0; i < emails.length; i += 150) {
      const part = await mailingApi(space).subscriptions(emails.slice(i, i + 150)).catch(() => []);
      for (const s of part) if (s.status) next.set(s.email, s.status);
    }
    setMailStatus(next);
  }

  async function clearMailStatus(email: string) {
    await mailingApi(space).clearContactStatus(email);
    setMailStatus((m) => { const n = new Map(m); n.delete(email.toLowerCase()); return n; });
  }

  async function restore(x: MailingExcludedContact) {
    if (!(await confirm({ title: 'Vrátit do skupiny?', message: `${x.email} bude zase dostávat kampaně skupiny „${s.name}“, pokud odpovídá jejím pravidlům.`, confirmLabel: 'Vrátit' }))) return;
    setExcludeErr(null);
    try {
      await includeInMailingSegment(s.id, x.email);
      setExcluded((list) => list.filter((e) => e.email !== x.email));
      setReload((k) => k + 1);
      onChanged?.();
    } catch (e) {
      setExcludeErr(e instanceof AffilmasterApiError ? e.message : 'Vrácení selhalo');
    }
  }

  async function exclude(c: MailingContactRow & { vocativeOk: boolean }) {
    if (!c.email) return;
    if (!(await confirm({ title: 'Vyřadit ze skupiny?', message: `${c.email} od skupiny „${s.name}“ už nedostane žádný mail. Vrátit ho jde v záložce Vyřazené.`, confirmLabel: 'Vyřadit', danger: true }))) return;
    setExcludeErr(null);
    try {
      await excludeFromMailingSegment(s.id, c.email);
      setContacts((list) => list?.filter((x) => (x.email ?? '').toLowerCase() !== c.email!.toLowerCase()) ?? null);
      setExcluded((list) => [...list, { email: c.email!.toLowerCase(), name: c.name || null, parentName: c.parentName, source: c.source, sourceId: c.sourceId }]);
      if (!c.vocativeOk) setMissingVocative((n) => Math.max(0, n - 1));
      onChanged?.();
    } catch (e) {
      setExcludeErr(e instanceof AffilmasterApiError ? e.message : 'Vyřazení selhalo');
    }
  }
  const [err, setErr] = useState<string | null>(null);

  useFeather([s, contacts, tab, excluded]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getMailingSegmentContacts(s.id);
        if (!cancelled) {
          setContacts([...r.rows].sort((a, b) => Number(a.vocativeOk) - Number(b.vocativeOk)));
          setMissingVocative(r.missingVocative);
          setExcluded(r.excluded ?? []);
          void loadMailStatus(r.rows);
        }
      } catch (e) {
        if (!cancelled) setErr(e instanceof AffilmasterApiError ? e.message : 'Nepodařilo se načíst kontakty');
      }
    })();
    return () => { cancelled = true; };
  }, [s.id, reload]);

  const ruleWord = s.ruleCount === 1 ? 'pravidlo' : s.ruleCount < 5 ? 'pravidla' : 'pravidel';
  const visibleContacts = tab === 'excluded' ? [] : contacts?.filter((c) => tab === 'all' || (c.email && !c.vocativeOk)) ?? [];
  const emailContacts = contacts?.filter((c) => c.email).length ?? 0;
  return (
    <ModalShell
      title="Detail skupiny"
      maxWidth={1180}
      onClose={onClose}
      className="am-segment-detail-modal"
      footer={
        <>
          <button type="button" className="am-modal-secondary" onClick={onClose}>Zavřít</button>
          <button type="button" className="am-modal-primary" onClick={onEdit}>
            <i data-feather="edit-2" aria-hidden="true" />
            Upravit skupinu
          </button>
        </>
      }
    >
      <section className="am-segment-detail-summary">
        <div className="am-segment-detail-identity">
          <span className="am-segment-detail-mark" style={{ backgroundColor: s.color }} aria-hidden="true">
            {s.name.slice(0, 2).toUpperCase()}
          </span>
          <div>
            <span>Skupina #{s.id}</span>
            <h3>{s.name}</h3>
            <div className="am-segment-detail-statuses">
              <span className={s.isActive ? 'is-positive' : ''}><i aria-hidden="true" />{s.isActive ? 'Aktivní' : 'Neaktivní'}</span>
              <span className={s.inList ? 'is-positive' : ''}><i aria-hidden="true" />{s.inList ? 'V seznamu kampaní' : 'Skrytý v seznamu'}</span>
            </div>
          </div>
        </div>
        <div className="am-segment-detail-metrics">
          <article><span>Kontakty</span><strong>{intFmt(contacts?.length ?? s.contactCount)}</strong><small>{missingVocative ? `${intFmt(missingVocative)} bez oslovení` : 'Všichni s oslovením'}</small></article>
          <article><span>Trh</span><strong>{s.country}</strong><small>Jazyk skupiny</small></article>
          <article><span>Priorita</span><strong>{s.priority}</strong><small>Pořadí vyhodnocení</small></article>
          <article><span>Pravidla</span><strong>{s.ruleCount}</strong><small>{s.ruleCount} {ruleWord}</small></article>
        </div>
      </section>

      <div className="am-segment-rule-summary">
        <span>Definice skupiny</span>
        <strong>{s.ruleSummary ?? 'Bez podmínek'}</strong>
        <span className="am-segment-colour"><i style={{ backgroundColor: s.color }} aria-hidden="true" />{s.color}</span>
      </div>

      <section className="am-segment-contacts">
        <header className="am-segment-contacts-head">
          <div>
            <span>Adresáti</span>
            <h3>Kontakty ve skupině</h3>
          </div>
          {contacts != null && (contacts.length > 0 || excluded.length > 0) && (
            <div className="am-segment-contact-tabs" role="tablist" aria-label="Kontakty skupiny">
              {([['missing', 'Bez vokativu', missingVocative], ['all', 'Všechny', contacts.length], ['excluded', 'Vyřazené', excluded.length]] as const).map(([k, label, count]) => (
                <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>
                  {label}<span>{intFmt(count)}</span>
                </button>
              ))}
            </div>
          )}
        </header>

        {err && <div className="am-segment-inline-message is-error"><i data-feather="alert-circle" aria-hidden="true" /><span>{err}</span></div>}
        {excludeErr && <div className="am-segment-inline-message is-error"><i data-feather="alert-circle" aria-hidden="true" /><span>{excludeErr}</span></div>}
        {contacts != null && contacts.length > 0 && tab !== 'excluded' && (
          missingVocative === 0 ? (
            <div className="am-vocative-notice is-ready">
              <i data-feather="check-circle" aria-hidden="true" />
              <div><strong>Oslovení jsou připravená</strong><span>Všech {intFmt(emailContacts)} kontaktů s e-mailem má platný vokativ.</span></div>
            </div>
          ) : (
            <div className="am-vocative-notice is-warning">
              <i data-feather="alert-triangle" aria-hidden="true" />
              <div>
                <strong>{intFmt(missingVocative)} {missingVocative === 1 ? 'kontakt mailing nedostane' : missingVocative < 5 ? 'kontakty mailing nedostanou' : 'kontaktů mailing nedostane'}</strong>
                <span>Klikni na <b>Doplnit</b>. U ruční adresy přidej vokativ za e-mail přímo v pravidle skupiny.</span>
              </div>
              <code>jan@firma.cz Jane</code>
            </div>
          )
        )}

        <div className="am-segment-contacts-scroll">
          <table className="am-segment-contacts-table">
            <thead>
              <tr>
                <th>Kontakt</th>
                <th>Vokativ</th>
                <th>Spojení</th>
                <th>Pozice / role</th>
                <th>{parentLabel}</th>
                <th>Zdroj</th>
                <th aria-label="Akce" />
              </tr>
            </thead>
            <tbody>
              {contacts == null && !err && (
                <tr className="am-contact-message"><td colSpan={7}>Načítám kontakty…</td></tr>
              )}
              {tab === 'excluded' && excluded.length === 0 && (
                <tr className="am-contact-message"><td colSpan={7}>Ze skupiny zatím nikdo nebyl vyřazen.</td></tr>
              )}
              {tab === 'excluded' && excluded.map((x) => (
                <tr key={x.email}>
                  <td className="am-contact-person" data-label="Kontakt"><strong>{x.name || x.email}</strong><small>Vyřazen ze skupiny</small></td>
                  <td data-label="Vokativ"><span className="am-contact-empty">—</span></td>
                  <td className="am-contact-connection" data-label="Spojení"><a href={`mailto:${x.email}`}>{x.email}</a></td>
                  <td data-label="Pozice / role"><span className="am-contact-empty">—</span></td>
                  <td data-label={parentLabel}>{x.parentName || <span className="am-contact-empty">—</span>}</td>
                  <td data-label="Zdroj">{x.source ? <span className={`am-contact-source is-${x.source}`}>{sourceLabel(x.source, space)}</span> : <span className="am-contact-empty">—</span>}</td>
                  <td className="am-contact-actions">
                    <button type="button" title="Vrátit do skupiny" aria-label={`Vrátit ${x.email} do skupiny`} onClick={() => restore(x)}><i data-feather="user-plus" aria-hidden="true" /></button>
                  </td>
                </tr>
              ))}
              {contacts != null && contacts.length === 0 && tab !== 'excluded' && (
                <tr className="am-contact-message"><td colSpan={7}>Žádné kontakty neodpovídají pravidlům skupiny.</td></tr>
              )}
              {contacts != null && tab === 'missing' && visibleContacts.length === 0 && (
                <tr className="am-contact-message is-ready"><td colSpan={7}><i data-feather="check-circle" aria-hidden="true" />Všechny kontakty mají vokativ.</td></tr>
              )}
              {visibleContacts.map((c) => {
                const href = cardHref(c, space);
                return (
                  <tr key={c.id}>
                    <td className="am-contact-person" data-label="Kontakt"><strong>{c.name || 'Bez jména'}</strong><small>Kontakt #{c.sourceId}</small></td>
                    <td data-label="Vokativ"><VocativeCell contact={c} onSaved={(v) => {
                      setContacts((list) => list?.map((x) => (x.id === c.id ? { ...x, vocative: v, vocativeOk: true } : x)) ?? null);
                      if (!c.vocativeOk) setMissingVocative((n) => Math.max(0, n - 1));
                    }} /></td>
                    <td className="am-contact-connection" data-label="Spojení">
                      {c.email && mailStatus.get(c.email.toLowerCase()) && <MailStatusDot status={mailStatus.get(c.email.toLowerCase())} onClear={() => clearMailStatus(c.email!)} />}
                      {c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : <span>Bez e-mailu</span>}
                      {c.phone ? <small>{c.phone}</small> : null}
                    </td>
                    <td data-label="Pozice / role">{c.position || <span className="am-contact-empty">—</span>}</td>
                    <td data-label={parentLabel}>{c.parentName || <span className="am-contact-empty">—</span>}</td>
                    <td data-label="Zdroj"><span className={`am-contact-source is-${c.source}`}>{sourceLabel(c.source, space)}</span></td>
                    <td className="am-contact-actions">
                      {href && <a href={href} target="_blank" rel="noopener" title="Otevřít kartu" aria-label={`Otevřít kartu kontaktu ${c.name || c.email}`}><i data-feather="external-link" aria-hidden="true" /></a>}
                      {c.email && <button type="button" title="Vyřadit ze skupiny" aria-label={`Vyřadit ${c.email} ze skupiny`} onClick={() => exclude(c)}><i data-feather="user-minus" aria-hidden="true" /></button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      {dialog}
    </ModalShell>
  );
}

const SEG_COUNTRIES = ['CZ', 'SK'] as const;
const RULE_METRICS: { code: string; label: string }[] = [
  { code: 'ma_email', label: 'Má e-mail' },
  { code: 'is_affiliate_kontakt', label: 'Affiliate kontakt' },
  { code: 'is_inzerent_kontakt', label: 'Inzerent kontakt' },
  { code: 'is_inzerent_exkluzivni', label: 'Inzerent exkluzivní' },
  { code: 'ma_telefon', label: 'Má telefon' },
  { code: 'ma_vokativ', label: 'Má vokativ' },
  { code: 'email_list', label: 'Ruční e-maily' },
  { code: 'email_exclude', label: 'Vyřazené e-maily' },
  { code: 'manual_member', label: 'Ruční přidání' },
];
const AP_RULE_LABELS: Record<string, string> = { is_inzerent_kontakt: 'Kontakt klienta' };
const AP_RULE_METRICS = RULE_METRICS
  .filter((m) => !['is_affiliate_kontakt', 'is_inzerent_exkluzivni'].includes(m.code))
  .map((m) => ({ ...m, label: AP_RULE_LABELS[m.code] ?? m.label }));
const RULE_OPERATORS: { code: string; label: string }[] = [
  { code: 'has', label: 'ANO' },
  { code: 'not_has', label: 'NE' },
];

interface DraftRule {
  metric: string;
  operator: string;
  value: string;
}

function SegmentEditModal({ segment, onClose, onSaved, metrics = RULE_METRICS }: { segment: MailingSegmentItem | null; onClose: () => void; onSaved: () => void; metrics?: { code: string; label: string }[] }) {
  const { confirm, dialog } = useConfirm();
  const isEdit = segment !== null;
  const [name, setName] = useState(segment?.name ?? '');
  const [country, setCountry] = useState<string>(segment?.country ?? 'CZ');
  const [color, setColor] = useState(segment?.color ?? '#007d88');
  const [priority, setPriority] = useState<number>(segment?.priority ?? 100);
  const [isActive, setIsActive] = useState<boolean>(segment?.isActive ?? true);
  const [inList, setInList] = useState<boolean>(segment?.inList ?? true);
  const [rules, setRules] = useState<DraftRule[]>(
    (segment?.rules ?? []).map((r) => ({ metric: r.metric, operator: r.op, value: String(r.value ?? '') })),
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useFeather([rules]);

  function addRule() {
    setRules((r) => [...r, { metric: metrics[0]!.code, operator: RULE_OPERATORS[0]!.code, value: '' }]);
  }
  function updateRule(i: number, patch: Partial<DraftRule>) {
    setRules((r) => r.map((rule, idx) => (idx === i ? { ...rule, ...patch } : rule)));
  }
  function removeRule(i: number) {
    setRules((r) => r.filter((_, idx) => idx !== i));
  }

  async function save() {
    if (name.trim().length < 2) { setErr('Název musí mít aspoň 2 znaky.'); return; }
    setBusy(true); setErr(null);
    const input = {
      name: name.trim(), country, color, priority, active: isActive, inList,
      rules: rules.filter((r) => r.metric && r.operator).map((r) => ({ metric: r.metric, op: r.operator, value: r.value })),
    };
    try {
      if (isEdit && segment) await updateMailingSegment(segment.id, input);
      else await createMailingSegment(input);
      onSaved();
    } catch (e) { setErr(e instanceof AffilmasterApiError ? e.message : 'Uložení se nezdařilo.'); }
    finally { setBusy(false); }
  }
  async function del() {
    if (!isEdit || !segment) return;
    if (!(await confirm({ title: 'Smazat skupinu?', message: `Skupina „${segment.name}“ se smaže.`, confirmLabel: 'Smazat', danger: true }))) return;
    setBusy(true); setErr(null);
    try { await deleteMailingSegment(segment.id); onSaved(); }
    catch (e) { setErr(e instanceof AffilmasterApiError ? e.message : 'Smazání se nezdařilo.'); }
    finally { setBusy(false); }
  }

  return (
    <ModalShell
      title={isEdit ? 'Upravit skupinu' : 'Nová skupina'}
      maxWidth={680}
      onClose={onClose}
      footer={
        <>
          {isEdit && <button className="btn btn-sm btn-outline-danger me-auto" disabled={busy} onClick={del}>Smazat skupinu</button>}
          <button className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={onClose}>Zavřít</button>
          <button className="btn btn-sm btn-primary" disabled={busy} onClick={save}>{busy ? 'Ukládám…' : 'Uložit'}</button>
        </>
      }
    >
      {err && <div className="alert alert-danger py-2">{err}</div>}
      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <label className="form-label fs-8 text-muted">Název</label>
          <input className="form-control form-control-sm" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="col-md-3">
          <label className="form-label fs-8 text-muted">Země</label>
          <select className="form-select form-select-sm" value={country} onChange={(e) => setCountry(e.target.value)}>
            {SEG_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label fs-8 text-muted">Priorita</label>
          <input type="number" className="form-control form-control-sm" value={priority} onChange={(e) => setPriority(Number(e.target.value))} />
        </div>
        <div className="col-md-6">
          <label className="form-label fs-8 text-muted">Barva</label>
          <div className="d-flex align-items-center gap-2">
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ width: 40, height: 31, padding: 2, border: '1px solid #eef0f3', borderRadius: 6 }} />
            <input className="form-control form-control-sm" value={color} onChange={(e) => setColor(e.target.value)} style={{ maxWidth: 120 }} />
          </div>
        </div>
        <div className="col-md-6 d-flex flex-column justify-content-end gap-1">
          <div className="form-check form-switch">
            <input className="form-check-input" type="checkbox" role="switch" id="mseg-active" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            <label className="form-check-label" htmlFor="mseg-active">Aktivní</label>
          </div>
          <div className="form-check form-switch">
            <input className="form-check-input" type="checkbox" role="switch" id="mseg-inlist" checked={inList} onChange={(e) => setInList(e.target.checked)} />
            <label className="form-check-label" htmlFor="mseg-inlist">Zobrazit v listu</label>
          </div>
        </div>
      </div>

      <div className="d-flex align-items-center justify-content-between mb-2">
        <h6 className="text-muted text-uppercase fs-8 mb-0">Pravidla</h6>
        <button className="btn btn-sm btn-outline-secondary" onClick={addRule}>+ Přidat pravidlo</button>
      </div>
      {rules.length === 0 ? (
        <p className="text-muted fs-8 mb-0">Zatím žádné pravidlo. Přidej první pomocí „+ Přidat pravidlo".</p>
      ) : (
        <div className="table-responsive-mobile"><table className="table aff-list-table align-middle mb-0">
          <thead>
            <tr><th>Metrika</th><th>Hodnota</th><th style={{ width: 40 }}></th></tr>
          </thead>
          <tbody>
            {rules.map((rule, i) => (
              <tr key={i}>
                <td>
                  <select className="form-select form-select-sm" value={rule.metric} onChange={(e) => updateRule(i, e.target.value === 'email_list' || e.target.value === 'email_exclude' || e.target.value === 'manual_member' ? { metric: e.target.value, operator: 'has' } : { metric: e.target.value })}>
                    {metrics.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
                  </select>
                </td>
                <td>
                  {rule.metric === 'manual_member' ? (
                    <span className="text-muted fs-8">
                      Kontakty se přidávají na kartě klienta nebo affiliate partnera ({rule.value ? String(rule.value).split(/\n+/).filter(Boolean).length : 0})
                    </span>
                  ) : rule.metric === 'email_exclude' ? (
                    <textarea
                      className="form-control form-control-sm"
                      rows={3}
                      placeholder="jeden e-mail na řádek, tyto kontakty skupina nikdy nezahrne"
                      value={rule.value}
                      onChange={(e) => updateRule(i, { value: e.target.value, operator: 'has' })}
                    />
                  ) : rule.metric === 'email_list' ? (
                    <textarea
                      className="form-control form-control-sm"
                      rows={3}
                      placeholder={'jeden kontakt na řádek: e-mail a vokativ\nnapř. jan.novak@firma.cz Jane'}
                      value={rule.value}
                      onChange={(e) => updateRule(i, { value: e.target.value, operator: 'has' })}
                    />
                  ) : (
                    <select className="form-select form-select-sm" value={rule.operator} onChange={(e) => updateRule(i, { operator: e.target.value })}>
                      {RULE_OPERATORS.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
                    </select>
                  )}
                </td>
                <td className="text-end">
                  <button className="btn btn-icon btn-sm btn-flush-dark" title="Smazat pravidlo" onClick={() => removeRule(i)}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
      {dialog}
    </ModalShell>
  );
}
